import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { BrowserCacheLocation, IPublicClientApplication, InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalService } from '@azure/msal-angular';
import { AngularDateHttpInterceptor, UserState, BASE_URL, AUTH_SCOPES, DIARY_HUB_URL, DiaryState, EMAIL_TEMPLATE_HUB_URL, EMAIL_HUB_URL, BlockUIState, MEDICAL_HUB_URL, MedicalState, MedicalService, DOMAIN, CORE_API_URL, EmailCampaignState, SYSTEM_USER_ID, TemplateEmailState, PracticeRatesService } from '@harlowe/core';
import { TealHealthPracticeRatesService } from './services/practice-rates.service';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { ConfirmationService, MessageService, OverlayService } from 'primeng/api';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MenuModule } from 'primeng/menu';
import { EditorModule } from 'primeng/editor';
import { NgxsModule, provideStore} from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { EncounterDraftEmailManagerItemFactory } from './components/main/email-manager/encounter-draft-email-manager';
import { DRAFT_EMAIL_ITEM_FACTORY } from './components/main/email-manager/email-manager.component';
import { DIARY_ITEM_FACTORY } from '@harlowe/core';
import { environment } from '../environments/environment';
import { UserInterfaceState } from './state/ui/ui-state';
import { EncounterState } from './state/encounters/encounter-state';
import { EmailState } from './state/encounter-email/email-state';
import { EncounterDiaryManagerItemFactory } from './components/main/correspondence/encounter-diary-manager';
import { TealHealthMedicalState } from './state/medical/health-medical-state';
import { TealHealthMedicalService } from './services/medical.service';
import { HEMI_BASE_URL } from './components/main/correspondence/encounter-diary-manager'

//MSAL Functions

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.harlowe.tealhealth.auth.clientId,
      authority: environment.harlowe.tealhealth.auth.authority,
      redirectUri: environment.harlowe.tealhealth.auth.redirectUri
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      allowRedirectInIframe: true,
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  });
}
  
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.harlowe.tealhealth.baseUri,environment.harlowe.tealhealth.auth.scopes);
  protectedResourceMap.set(environment.harlowe.tealhealth.coreApiUri, environment.harlowe.tealhealth.auth.scopes);
  return {
    interactionType: InteractionType.Popup,
    protectedResourceMap
  };
  }
  
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { 
    interactionType: InteractionType.Popup,
    authRequest: {
      scopes: environment.harlowe.tealhealth.auth.scopes
    }
    };
  }


export const appConfig: ApplicationConfig = 
{
  providers: [

    provideRouter(routes),
    // provideAnimations(),
    provideHttpClient(withInterceptorsFromDi(),withFetch()),
    
    //For @harlowe/core api services
    { provide: BASE_URL, useValue: environment.harlowe.tealhealth.baseUri },
    { provide: CORE_API_URL, useValue: environment.harlowe.tealhealth.coreApiUri },
    { provide: HEMI_BASE_URL, useValue: environment.harlowe.hemi.baseUri},
    { provide: AUTH_SCOPES, useValue: environment.harlowe.tealhealth.auth.scopes},
    { provide: DIARY_HUB_URL, useValue: environment.harlowe.tealhealth.baseUri + environment.harlowe.tealhealth.signaling.endpoints.encounterDiaries},
    { provide: EMAIL_TEMPLATE_HUB_URL, useValue: environment.harlowe.tealhealth.baseUri + environment.harlowe.tealhealth.signaling.endpoints.emailTemplates},
    { provide: EMAIL_HUB_URL, useValue: environment.harlowe.tealhealth.baseUri + environment.harlowe.tealhealth.signaling.endpoints.email},
    { provide: MEDICAL_HUB_URL, useValue: environment.harlowe.tealhealth.baseUri + environment.harlowe.tealhealth.signaling.endpoints.medical},
    { provide: DOMAIN, useValue: environment.harlowe.tealhealth.domain },
    { provide: SYSTEM_USER_ID, useValue: environment.systemUserId },

    //MSAL Stuff
    {
        provide: HTTP_INTERCEPTORS,
        useClass: MsalInterceptor,
        multi: true
    },
    {
        provide: MSAL_INSTANCE,
        useFactory: MSALInstanceFactory
    },
    {
        provide: MSAL_GUARD_CONFIG,
        useFactory: MSALGuardConfigFactory
    },
    {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MessageService,
    ConfirmationService,
    //Date Intercepter
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AngularDateHttpInterceptor,
      multi: true
    },
    //NGSX
    provideStore([UserState, EncounterState, UserInterfaceState, BlockUIState, EmailState, DiaryState, MedicalState, EmailCampaignState, TemplateEmailState]),

    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      OverlayService,
      MenuModule,
      EditorModule,
    ),
    { provide: DIARY_ITEM_FACTORY, useClass: EncounterDiaryManagerItemFactory},
    { provide: DRAFT_EMAIL_ITEM_FACTORY, useClass: EncounterDraftEmailManagerItemFactory},
    { provide: MedicalService, useClass: TealHealthMedicalService },
    { provide: PracticeRatesService, useClass: TealHealthPracticeRatesService }
  ]
  }
